<template>
  <div class="bg-background text-foreground min-h-screen">
    <!-- Toast Notification -->
    <Toast
      :show="showToast"
      :type="toastType"
      :title="toastTitle"
      :message="toastMessage"
      @close="showToast = false"
    />

    <div class="w-full px-4 sm:px-6 py-5">

      <!-- Page Header -->
      <div class="flex items-start justify-between gap-4 mb-5">
        <div class="flex items-start gap-3 min-w-0">
          <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-bookmark text-primary text-lg"></i>
          </div>
          <div class="min-w-0">
            <h1 class="text-xl font-bold leading-tight">Saved Jobs</h1>
            <p class="text-sm text-muted-foreground leading-snug">
              Jobs you've saved for later. Apply to these positions or remove them from your list.
            </p>
          </div>
        </div>
        <span
          v-if="!loading && !error && savedJobs.length > 0"
          class="flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground border border-border"
        >
          {{ savedJobs.length }}
        </span>
      </div>

      <!-- Loading State -->
      <div v-if="loading">
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
        <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="i in 4"
            :key="i"
            class="bg-card border border-border rounded-xl p-5"
          >
            <div class="flex gap-4">
              <div class="w-12 h-12 rounded-xl bg-muted animate-pulse flex-shrink-0"></div>
              <div class="flex-1 space-y-3">
                <div class="h-3 w-2/5 rounded bg-muted animate-pulse"></div>
                <div class="h-3 w-1/4 rounded bg-muted animate-pulse"></div>
                <div class="flex gap-2">
                  <div class="h-3 w-16 rounded-full bg-muted animate-pulse"></div>
                  <div class="h-3 w-20 rounded-full bg-muted animate-pulse"></div>
                  <div class="h-3 w-24 rounded-full bg-muted animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 mb-5"
      >
        <div class="flex items-start gap-3">
          <i class="fas fa-exclamation-triangle text-red-600 dark:text-red-400 mt-0.5"></i>
          <div>
            <p class="font-semibold text-red-700 dark:text-red-300 text-sm">Something went wrong</p>
            <p class="text-sm text-red-700/90 dark:text-red-300/90 mt-0.5">{{ error }}</p>
            <button
              @click="fetchSavedJobs"
              class="mt-3 px-4 py-1.5 rounded-lg border border-red-500/30 bg-card hover:bg-red-500/10 text-red-700 dark:text-red-300 text-sm font-medium transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="savedJobs.length === 0"
        class="bg-card border border-border rounded-xl"
      >
        <div class="flex flex-col items-center justify-center py-16 text-center">
          <div class="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-3">
            <i class="far fa-bookmark text-muted-foreground text-xl"></i>
          </div>
          <div class="text-sm font-semibold mb-1">No saved jobs</div>
          <div class="text-[12px] text-muted-foreground mb-6 max-w-xs mx-auto">
            Jobs you save will appear here. Browse jobs and click the bookmark icon to save them.
          </div>
          <router-link
            to="/job-filters"
            class="inline-flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold rounded-lg transition"
          >
            <i class="fas fa-search text-xs"></i>
            Browse Jobs
          </router-link>
        </div>
      </div>

      <!-- Results List -->
      <div v-else>

        <!-- Sort / Filter toolbar -->
        <div class="flex items-center justify-between gap-3 mb-4">
          <div class="flex items-center gap-2">
            <span class="text-sm text-muted-foreground">Sort by</span>
            <select
              v-model="savedJobsSort"
              class="h-9 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer"
            >
              <option value="date_saved_desc">Date saved (newest)</option>
              <option value="date_saved_asc">Date saved (oldest)</option>
              <option value="title_asc">Title (A-Z)</option>
              <option value="title_desc">Title (Z-A)</option>
            </select>
          </div>
          <button
            type="button"
            class="h-9 px-3 rounded-lg border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-muted transition"
            @click="showFilterToast"
          >
            <i class="fas fa-filter mr-1.5"></i>
            Filter
          </button>
        </div>

        <!-- Job Cards Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div
            v-for="job in paginatedSavedJobs"
            :key="job.id"
            class="bg-card border border-border rounded-xl p-5 flex flex-col"
          >
            <!-- Card top: logo + title + actions -->
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-start gap-3 min-w-0">
                <!-- Employer Logo -->
                <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 border border-border flex items-center justify-center">
                  <span class="text-primary text-sm font-bold">{{
                    employerInitials(
                      job.job_details?.employer || job.employer || job,
                    )
                  }}</span>
                </div>

                <div class="min-w-0">
                  <h3 class="text-base font-semibold text-foreground hover:text-primary leading-snug">
                    <router-link :to="getJobDetailPath(job)">
                      {{
                        job.job_details?.title ||
                        job.title ||
                        "Untitled Position"
                      }}
                    </router-link>
                  </h3>
                  <p class="text-sm text-muted-foreground mt-0.5">
                    {{
                      job.job_details?.employer?.name ||
                      (job.job_details?.employer &&
                      typeof job.job_details.employer === "string"
                        ? job.job_details.employer
                        : "") ||
                      job.employer?.name ||
                      job.employer ||
                      "Employer"
                    }}
                    <span
                      v-if="job.job_details?.employer?.total_posts"
                      class="text-muted-foreground text-sm ml-1"
                    >({{ job.job_details.employer.total_posts }} jobs posted)</span>
                    <span
                      v-else-if="job.employer?.total_posts"
                      class="text-muted-foreground text-sm ml-1"
                    >({{ job.employer.total_posts }} jobs posted)</span>
                  </p>
                </div>
              </div>

              <!-- Source badge + remove button -->
              <div class="flex items-center gap-2 flex-shrink-0">
                <span
                  v-if="getJobSource(job) === 'olj'"
                  class="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground border border-border"
                >
                  External
                </span>
<!-- HIDDEN: LinkedIn -->
                <!-- <span
                  v-else-if="getJobSource(job) === 'linkedin'"
                  class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold"
                  style="color: #0a66c2; background-color: #e7f3ff"
                >
                  LinkedIn
                </span> -->
<!-- /HIDDEN: LinkedIn -->
                <button
                  @click="removeJob(job)"
                  class="p-2 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 rounded-lg border border-border transition"
                  aria-label="Remove saved job"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>

            <!-- Status + metadata pills -->
            <div class="mt-4 flex flex-wrap gap-2">
              <span
                v-if="job.job_details?.closed || job.closed"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/30"
              >
                <i class="fas fa-times-circle mr-1"></i>
                Closed
              </span>
              <span
                v-else-if="job.job_details && job.job_details.userHasApplied"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
              >
                <i class="fas fa-check mr-1"></i>
                Applied
              </span>
              <span
                v-if="job.job_details?.job_type || job.job_type"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-muted/50 text-foreground border border-border"
              >
                <i class="fas fa-briefcase mr-1"></i>
                {{ job.job_details?.job_type || job.job_type }}
              </span>
              <span
                v-if="job.job_details?.salary || job.salary"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-muted/50 text-foreground border border-border"
              >
                <i class="fas fa-dollar-sign mr-1"></i>
                {{ job.job_details?.salary || job.salary }}
              </span>
              <span
                v-if="job.job_details?.date_posted || job.date_posted"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-muted/50 text-foreground border border-border"
              >
                <i class="far fa-calendar-alt mr-1"></i>
                {{ job.job_details?.date_posted || job.date_posted }}
              </span>
            </div>

            <!-- Tags -->
            <div
              v-if="
                (job.job_details?.tags && job.job_details.tags.length > 0) ||
                (job.tags && job.tags.length > 0)
              "
              class="mt-3 flex flex-wrap gap-2"
            >
              <span
                v-for="tag in job.job_details?.tags || job.tags"
                :key="tag.id"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-muted/50 text-foreground border border-border"
              >
                {{ tag.name }}
              </span>
            </div>

            <!-- Description -->
            <p
              v-if="job.job_details?.description || job.description"
              class="mt-3 text-sm text-muted-foreground line-clamp-2"
            >
              {{
                truncateDescription(
                  job.job_details?.description || job.description,
                )
              }}
            </p>

            <!-- Action buttons -->
            <div class="mt-auto pt-5 flex items-center gap-2">
              <router-link
                v-if="
                  !(job.job_details?.closed || job.closed) &&
                  !(job.job_details && job.job_details.userHasApplied)
                "
                :to="getJobDetailPath(job)"
                class="flex-1 inline-flex items-center justify-center h-9 px-4 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold transition"
              >
                Apply
              </router-link>
              <button
                v-else-if="job.job_details?.closed || job.closed"
                class="flex-1 inline-flex items-center justify-center h-9 px-4 rounded-lg bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/30 text-sm font-medium"
                disabled
              >
                Job Closed
              </button>
              <button
                v-else
                class="flex-1 inline-flex items-center justify-center h-9 px-4 rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-sm font-medium"
                disabled
              >
                Applied
              </button>

              <router-link
                :to="getJobDetailPath(job)"
                class="inline-flex items-center justify-center h-9 px-4 rounded-lg border border-border bg-card hover:bg-muted text-sm font-medium transition"
              >
                Details
              </router-link>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="mt-8 flex justify-center"
        >
          <nav class="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="w-8 h-8 rounded-md flex items-center justify-center transition text-sm"
              :class="[
                currentPage === 1
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : 'text-muted-foreground hover:bg-muted',
              ]"
            >
              <i class="fas fa-chevron-left text-xs"></i>
            </button>

            <button
              v-for="page in displayedPages"
              :key="page"
              @click="goToPage(page)"
              class="w-8 h-8 rounded-md flex items-center justify-center transition text-sm font-medium"
              :class="[
                page === currentPage
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              ]"
            >
              {{ page }}
            </button>

            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="w-8 h-8 rounded-md flex items-center justify-center transition text-sm"
              :class="[
                currentPage === totalPages
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : 'text-muted-foreground hover:bg-muted',
              ]"
            >
              <i class="fas fa-chevron-right text-xs"></i>
            </button>
          </nav>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import Toast from "@/components/Toast.vue";
import { buildJobDetailsPath } from "@/utils/slug";

export default {
  name: "SavedJobsPage",
  components: {
    Toast,
  },
  setup() {
    const store = useStore();

    // Toast State
    const showToast = ref(false);
    const toastType = ref("success");
    const toastTitle = ref("");
    const toastMessage = ref("");

    // Computed properties
    const savedJobs = computed(() => {
      const jobs = store.getters["savedJobs/savedJobs"];
      return jobs;
    });

    const loading = computed(() => store.getters["savedJobs/loading"]);
    const error = computed(() => store.getters["savedJobs/error"]);

    const savedJobsSort = ref("date_saved_desc");

    const currentPage = ref(1);
    const itemsPerPage = ref(10);

    const sortedSavedJobs = computed(() => {
      const list = Array.isArray(savedJobs.value) ? [...savedJobs.value] : [];

      const getTitle = (item) =>
        String(
          item?.job_details?.title || item?.job?.title || item?.title || "",
        ).toLowerCase();

      const getSavedTime = (item) => {
        const raw = item?.date_saved || item?.saved_at || item?.created_at;
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
      };

      switch (savedJobsSort.value) {
        case "date_saved_asc":
          list.sort((a, b) => getSavedTime(a) - getSavedTime(b));
          break;
        case "title_asc":
          list.sort((a, b) => getTitle(a).localeCompare(getTitle(b)));
          break;
        case "title_desc":
          list.sort((a, b) => getTitle(b).localeCompare(getTitle(a)));
          break;
        case "date_saved_desc":
        default:
          list.sort((a, b) => getSavedTime(b) - getSavedTime(a));
          break;
      }

      return list;
    });

    const totalPages = computed(() => {
      const total = sortedSavedJobs.value.length;
      return Math.max(1, Math.ceil(total / itemsPerPage.value));
    });

    const paginatedSavedJobs = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      return sortedSavedJobs.value.slice(start, end);
    });

    const displayedPages = computed(() => {
      const total = totalPages.value;
      const current = currentPage.value;
      const windowSize = 5;

      if (total <= windowSize) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      let start = Math.max(1, current - Math.floor(windowSize / 2));
      let end = start + windowSize - 1;

      if (end > total) {
        end = total;
        start = end - windowSize + 1;
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });

    const goToPage = (page) => {
      if (typeof page !== "number") return;
      if (page < 1 || page > totalPages.value) return;
      currentPage.value = page;
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value += 1;
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value -= 1;
      }
    };

    watch(savedJobsSort, () => {
      currentPage.value = 1;
    });

    watch(
      () => sortedSavedJobs.value.length,
      () => {
        if (currentPage.value > totalPages.value) {
          currentPage.value = totalPages.value;
        }
      },
    );

    const getJobDetailPath = (jobItem) => {
      if (!jobItem) {
        return buildJobDetailsPath("", "");
      }
      const id = jobItem.job_details?.id ?? jobItem.job?.id ?? jobItem.id;
      const title =
        jobItem.job_details?.title ?? jobItem.job?.title ?? jobItem.title;
      return buildJobDetailsPath(title, id);
    };

    // Fetch saved jobs on component mount
    onMounted(() => {
      fetchSavedJobs();
    });

    // Methods
    const fetchSavedJobs = async () => {
      try {
        await store.dispatch("savedJobs/fetchSavedJobs");

        // Debug log
      } catch (error) {
        console.error("Error fetching saved jobs:", error);
      }
    };

    const removeJob = async (job) => {
      try {
        // Get the job ID - handle different possible structures
        const jobId =
          job.id ||
          (job.job_details && job.job_details.id) ||
          (job.job && job.job.id);


        if (!jobId) {
          console.error("Could not determine job ID for removal", job);
          return;
        }

        const success = await store.dispatch("savedJobs/removeJob", jobId);

        if (success) {
          // Force refresh the saved jobs list to update UI
          await fetchSavedJobs();

          // Show success toast notification
          toastType.value = "success";
          toastTitle.value = "Job Removed";
          toastMessage.value = "The job has been removed from your saved jobs.";
          showToast.value = true;

          // Hide toast after 3 seconds
          setTimeout(() => {
            showToast.value = false;
          }, 3000);
        }
      } catch (error) {
        console.error("Error removing job:", error);

        // Show error toast
        toastType.value = "error";
        toastTitle.value = "Error";
        toastMessage.value =
          "An error occurred while removing the job. Please try again.";
        showToast.value = true;

        // Hide toast after 3 seconds
        setTimeout(() => {
          showToast.value = false;
        }, 3000);
      }
    };

    const employerInitials = (employer) => {
      if (!employer) return "CO";

      let employerName;
      if (typeof employer === "string") {
        employerName = employer;
      } else if (employer.name) {
        employerName = employer.name;
      } else {
        return "CO";
      }

      return employerName
        .split(" ")
        .map((word) => word[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    };

    const truncateDescription = (text) => {
      if (!text) return "";
      return text.length > 150 ? text.slice(0, 150) + "..." : text;
    };

    const getJobSource = (jobItem) => {
      const src =
        jobItem?.job_details?.source ?? jobItem?.source ?? jobItem?.job?.source;
      if (!src) return null;
      return String(src).toLowerCase();
    };

    const showFilterToast = () => {
      toastType.value = "success";
      toastTitle.value = "Coming soon";
      toastMessage.value = "Saved jobs filtering will be available soon.";
      showToast.value = true;
      setTimeout(() => {
        showToast.value = false;
      }, 3000);
    };

    return {
      savedJobs,
      loading,
      error,
      showToast,
      toastType,
      toastTitle,
      toastMessage,
      fetchSavedJobs,
      removeJob,
      getJobDetailPath,
      employerInitials,
      truncateDescription,
      getJobSource,
      savedJobsSort,
      sortedSavedJobs,
      currentPage,
      itemsPerPage,
      totalPages,
      paginatedSavedJobs,
      displayedPages,
      goToPage,
      nextPage,
      prevPage,
      showFilterToast,
    };
  },
};
</script>

<style>
/* Utility classes */
.line-clamp-2 {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
